import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { ContentPerformance, EngagementMetrics } from "../../../shared/types";

// ── Types ──

export interface PerformanceSummaryData {
  total_likes: number;
  total_shares: number;
  total_comments: number;
  total_clicks: number;
  total_impressions: number;
  per_platform: Record<string, EngagementMetrics>;
  piece_count: number;
}

// ── Queries ──

/** Fetch performance data for a specific content run. Polls every 30s. */
export function useRunPerformance(runId: string | undefined) {
  return useQuery({
    queryKey: ["performance", runId],
    queryFn: () =>
      api.get<{ data: ContentPerformance[] }>(
        `/api/performance/${runId}`,
      ),
    enabled: !!runId,
    refetchInterval: 30_000,
  });
}

/** Fetch aggregated performance summary for a brand. */
export function usePerformanceSummary(brandId: string | undefined) {
  return useQuery({
    queryKey: ["performance-summary", brandId],
    queryFn: () =>
      api.get<{ data: PerformanceSummaryData }>(
        `/api/performance/summary?brand_id=${brandId}`,
      ),
    enabled: !!brandId,
  });
}

// ── Mutations ──

/** Trigger fresh metric collection for a content run. */
export function useCollectMetrics(runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ data: ContentPerformance[] }>(
        `/api/performance/collect/${runId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["performance", runId] });
      qc.invalidateQueries({ queryKey: ["performance-summary"] });
    },
  });
}
